import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';
import { PostHogProvider } from 'posthog-js/react';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import CalendarPage from './routes/CalendarPage';
import RequestsPage from './routes/RequestsPage';
import NoAccountPage from './routes/NoAccount';
import TeamPage from './routes/TeamPage';
import CustomersPage from './routes/Customers';
import ReportsPage from './routes/ReportsPage';
import ChatPage from './routes/ChatPage';
import SubscriptionPage from './routes/SubscriptionPage';
import ExternalRedirectPage from './routes/ExternalRedirectPage';
import DashboardLayout from './components/global/DashboardLayout';
import { ROUTES } from './constants/global';
import ShopPage from './routes/ShopPage';
import theme from './constants/theme';
import WaiversPage from './routes/WaiversPage';
import CheckoutPage from './routes/CheckoutsPage';
import PaymentsPage from './routes/PaymentsPage';
import BookingLinksPage from './routes/BookingLinksPage';
import MarketingPage from './routes/MarketingPage';
import ItemsPage from './routes/ItemsPage';
import ImportPage from './routes/ImportPage';
import EmployeeWaiverPage from './routes/EmployeeWaiverPage';
import PlansPage from './routes/PlansPage';
import NotificationsPage from './routes/NotificationsPage';

if (window?.location?.hostname !== 'localhost')
  LicenseInfo.setLicenseKey(
    '9a3133ae7bdefeb78723bc2edce46193Tz03Nzg0MyxFPTE3MzA0ODIyNTcwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI=',
  );

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

const posthogOptions = {
  api_host: 'https://us.i.posthog.com',
};

root.render(
  <React.StrictMode>
    <PostHogProvider
      apiKey={process.env.REACT_APP_POSTHOG_KEY!}
      options={posthogOptions}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <Routes>
              <Route element={<App />}>
                {/* Layout with no nav */}
                <Route
                  element={
                    <DashboardLayout
                      sideNav={false}
                      primaryNav={false}
                      noUser
                    />
                  }>
                  <Route
                    path={`${ROUTES.waiverRequest}/:waiverRequestId/embed`}
                    element={<EmployeeWaiverPage />}
                  />
                </Route>

                {/* Layout with both side and primary nav */}
                <Route element={<DashboardLayout />}>
                  <Route path="/" element={<CalendarPage />} />
                  <Route path="/calendar/" element={<CalendarPage />} />
                  <Route
                    path="/calendar/:appointmentId?"
                    element={<CalendarPage />}
                  />
                  <Route path="/checkouts/" element={<CheckoutPage />} />
                  <Route path="/requests/" element={<RequestsPage />} />
                  <Route
                    path="/requests/:appointmentId?"
                    element={<RequestsPage />}
                  />
                  <Route path="/team/" element={<TeamPage />} />
                  <Route
                    path="/customers/:customerId?"
                    element={<CustomersPage />}
                  />
                  <Route path="/reports/" element={<ReportsPage />} />
                  <Route
                    path={ROUTES.subscription}
                    element={<SubscriptionPage />}
                  />
                  <Route path={ROUTES.plans} element={<PlansPage />} />
                  <Route path={ROUTES.chat} element={<ChatPage />} />
                  <Route
                    path={`${ROUTES.chat}/:conversationId`}
                    element={<ChatPage />}
                  />
                  <Route path={ROUTES.shop} element={<ShopPage />} />
                  <Route path={ROUTES.waivers} element={<WaiversPage />} />
                  <Route
                    path={`${ROUTES.waivers}/:waiverId`}
                    element={<WaiversPage />}
                  />
                  <Route path={ROUTES.payments} element={<PaymentsPage />} />
                  <Route path={ROUTES.import} element={<ImportPage />} />
                  <Route
                    path={ROUTES.bookingLinks}
                    element={<BookingLinksPage />}
                  />
                  <Route path={ROUTES.marketing} element={<MarketingPage />} />
                  <Route path={ROUTES.items} element={<ItemsPage />} />
                  <Route
                    path={`${ROUTES.waiverRequest}/:waiverRequestId`}
                    element={<EmployeeWaiverPage />}
                  />
                  <Route
                    path={ROUTES.notifications}
                    element={<NotificationsPage />}
                  />
                </Route>

                {/* Layout with only primary nav */}
                <Route element={<DashboardLayout sideNav={false} noUser />}>
                  <Route path="/signup/" element={<NoAccountPage />} />
                </Route>

                <Route
                  path="/external-redirect"
                  element={<ExternalRedirectPage />}
                />
                <Route
                  path="/*"
                  element={
                    <main style={{ padding: '1rem' }}>
                      <p>There&apos;s nothing here!</p>
                    </main>
                  }
                />
              </Route>
            </Routes>
          </BrowserRouter>
        </ThemeProvider>
      </LocalizationProvider>
    </PostHogProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
